import { useState, useEffect } from 'react';
import { 
    DollarSign, 
    AlertTriangle, 
    Clock, 
    Calendar,
    Building2,
    FileText,
    RefreshCw,
    Loader2,
    ChevronDown,
    ChevronUp,
    TrendingUp,
    AlertCircle,
    Download,
    CheckCircle,
    Truck
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ReceivablesPage() {
    const [data, setData] = useState(null);
    const [dispatcherData, setDispatcherData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [dispatcherLoading, setDispatcherLoading] = useState(true);
    const [expandedBroker, setExpandedBroker] = useState(null);
    const [generatingInvoice, setGeneratingInvoice] = useState({});
    const [activeTab, setActiveTab] = useState('broker');

    useEffect(() => {
        fetchReceivablesData();
        fetchDispatcherData();
    }, []);

    const fetchReceivablesData = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${API_URL}/api/receivables/dashboard`, {
                credentials: 'include'
            });

            if (response.ok) {
                const result = await response.json();
                setData(result);
            } else {
                toast.error('Failed to load receivables data');
            }
        } catch (error) {
            console.error('Failed to fetch receivables data:', error);
            toast.error('Failed to load receivables data');
        } finally {
            setLoading(false);
        }
    };

    const fetchDispatcherData = async () => {
        setDispatcherLoading(true);
        try {
            const response = await fetch(`${API_URL}/api/dispatcher/revenue/dashboard`, {
                credentials: 'include'
            });

            if (response.ok) {
                const result = await response.json();
                setDispatcherData(result);
            } else {
                // If endpoint doesn't exist or fails, just set to null
                setDispatcherData(null);
            }
        } catch (error) {
            console.error('Failed to fetch dispatcher data:', error);
            setDispatcherData(null);
        } finally {
            setDispatcherLoading(false);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount || 0);
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        try {
            return new Date(dateStr).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
            });
        } catch {
            return 'N/A';
        }
    };

    const getAgingColor = (risk) => {
        if (risk === 'red') return 'text-red-400';
        if (risk === 'amber') return 'text-amber-400';
        return 'text-green-400';
    };

    const getAgingBgColor = (risk) => {
        if (risk === 'red') return 'bg-red-500/20 border-red-500/30';
        if (risk === 'amber') return 'bg-amber-500/20 border-amber-500/30';
        return 'bg-green-500/20 border-green-500/30';
    };

    // Generate dispatcher invoice for a load
    const handleGenerateDispatchInvoice = async (loadId) => {
        setGeneratingInvoice(prev => ({ ...prev, [loadId]: true }));
        try {
            const response = await fetch(`${API_URL}/api/dispatcher/invoice/${loadId}`, {
                method: 'POST',
                credentials: 'include'
            });

            if (response.ok) {
                const invoiceData = await response.json();
                // Generate PDF
                generateDispatchInvoicePDF(invoiceData);
                toast.success('Dispatch invoice generated');
                // Refresh data
                fetchDispatcherData();
            } else {
                const error = await response.json();
                toast.error(error.detail || 'Failed to generate invoice');
            }
        } catch (error) {
            console.error('Failed to generate invoice:', error);
            toast.error('Failed to generate invoice');
        } finally {
            setGeneratingInvoice(prev => ({ ...prev, [loadId]: false }));
        }
    };

    // Generate PDF for dispatcher invoice
    const generateDispatchInvoicePDF = (data) => {
        const doc = new jsPDF();
        
        // Header - Big T's Logistics branding
        doc.setFillColor(249, 115, 22); // Orange
        doc.rect(0, 0, 220, 35, 'F');
        
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(24);
        doc.setFont('helvetica', 'bold');
        doc.text("Big T's Logistics", 14, 18);
        
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.text("Dispatch Services", 14, 26);
        
        // Invoice info
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(12);
        doc.text(`Invoice: ${data.invoice_id}`, 145, 18);
        doc.setFontSize(10);
        doc.text(`Date: ${data.invoice_date}`, 145, 26);
        
        // Reset colors
        doc.setTextColor(0, 0, 0);
        
        // Bill To
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text("BILL TO:", 14, 50);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        doc.text(`Carrier / Truck: ${data.truck_id}`, 14, 58);
        doc.text(`Broker: ${data.broker_name}`, 14, 65);
        
        // Load Details
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.text("LOAD DETAILS:", 14, 82);
        
        autoTable(doc, {
            startY: 88,
            head: [['Description', 'Route', 'Gross Rate', 'Commission %', 'Dispatch Fee']],
            body: [
                [
                    `Load ${data.load_id}`,
                    `${data.origin} → ${data.destination}`,
                    `$${data.gross_rate.toFixed(2)}`,
                    `${data.commission_rate}%`,
                    `$${data.dispatcher_fee.toFixed(2)}`
                ]
            ],
            theme: 'striped',
            headStyles: { fillColor: [249, 115, 22] },
            styles: { fontSize: 9 }
        });
        
        // Total
        const finalY = doc.lastAutoTable.finalY + 10;
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(14);
        doc.text("TOTAL DUE:", 120, finalY);
        doc.setTextColor(34, 197, 94); // Green
        doc.text(`$${data.dispatcher_fee.toFixed(2)}`, 170, finalY);
        
        // Due Date
        doc.setTextColor(220, 38, 38); // Red
        doc.setFontSize(10);
        doc.text(`DUE DATE: ${data.due_date}`, 120, finalY + 8);
        
        // Payment Terms
        doc.setTextColor(0, 0, 0);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.text("Payment Terms: Net 7 Days", 14, finalY + 25);
        doc.text("Thank you for your business!", 14, finalY + 32);
        
        // Footer
        doc.setFillColor(39, 39, 42); // Zinc-800
        doc.rect(0, 275, 220, 22, 'F');
        doc.setTextColor(161, 161, 170); // Zinc-400
        doc.setFontSize(8);
        doc.text("Big T's Logistics - Professional Dispatch Services", 14, 284);
        doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 290);
        
        // Download
        doc.save(`BigTs_Invoice_${data.invoice_id}.pdf`);
    };

    // Mark dispatcher invoice as paid
    const handleMarkPaid = async (loadId) => {
        try {
            const response = await fetch(`${API_URL}/api/dispatcher/invoice/${loadId}/mark-paid`, {
                method: 'POST',
                credentials: 'include'
            });

            if (response.ok) {
                toast.success('Invoice marked as paid');
                fetchDispatcherData();
            } else {
                const error = await response.json();
                toast.error(error.detail || 'Failed to mark as paid');
            }
        } catch (error) {
            console.error('Failed to mark as paid:', error);
            toast.error('Failed to mark as paid');
        }
    };

    return (
        <div className="space-y-6 animate-fade-in" data-testid="receivables-page">
            {/* Critical Alert Banner */}
            {data?.critical_alert?.has_critical && (
                <div 
                    className="bg-red-500/10 border border-red-500/30 rounded-sm p-4 flex items-center gap-3"
                    data-testid="critical-alert-banner"
                >
                    <AlertTriangle className="w-6 h-6 text-red-400 shrink-0" />
                    <div>
                        <p className="text-red-400 font-medium">
                            {data.critical_alert.message}
                        </p>
                    </div>
                </div>
            )}

            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-white tracking-tight uppercase flex items-center gap-3">
                        <DollarSign className="w-6 h-6 text-orange-500" />
                        Receivables Command
                    </h1>
                    <p className="text-zinc-500 text-sm mt-1">
                        Unpaid dispatch revenue visibility to prevent cash flow surprises
                    </p>
                </div>
                <Button 
                    variant="outline"
                    onClick={() => { fetchReceivablesData(); fetchDispatcherData(); }}
                    disabled={loading || dispatcherLoading}
                    className="border-zinc-700"
                    data-testid="refresh-receivables-btn"
                >
                    <RefreshCw className={`w-4 h-4 mr-2 ${loading || dispatcherLoading ? 'animate-spin' : ''}`} />
                    Refresh
                </Button>
            </div>

            {/* Tabs for Broker vs Dispatcher */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="bg-zinc-800 border-zinc-700">
                    <TabsTrigger value="broker" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white">
                        <Building2 className="w-4 h-4 mr-2" />
                        Broker Receivables
                    </TabsTrigger>
                    <TabsTrigger value="dispatcher" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white">
                        <Truck className="w-4 h-4 mr-2" />
                        Big T's Logistics Fees
                    </TabsTrigger>
                </TabsList>

                {/* Broker Receivables Tab */}
                <TabsContent value="broker" className="mt-6">
            {loading ? (
                <div className="flex flex-col items-center justify-center py-12">
                    <Loader2 className="w-10 h-10 text-orange-500 animate-spin mb-4" />
                    <p className="text-zinc-400">Loading receivables data...</p>
                </div>
            ) : !data ? (
                <Card className="bg-zinc-900/50 border-zinc-800">
                    <CardContent className="flex flex-col items-center justify-center py-12">
                        <DollarSign className="w-12 h-12 text-zinc-600 mb-4" />
                        <h3 className="text-lg font-medium text-white mb-2">No Data Available</h3>
                        <p className="text-zinc-400 text-center">
                            Create invoices to track receivables.
                        </p>
                    </CardContent>
                </Card>
            ) : (
                <>
                    {/* Summary Cards */}
                    <div className="grid grid-cols-4 gap-4" data-testid="summary-cards">
                        {/* Total Outstanding */}
                        <Card className="bg-zinc-900/50 border-zinc-800">
                            <CardContent className="p-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-orange-500/20 rounded-sm flex items-center justify-center">
                                        <DollarSign className="w-5 h-5 text-orange-400" />
                                    </div>
                                    <div>
                                        <p className="text-xs text-zinc-500 uppercase">Total Outstanding</p>
                                        <p className="text-2xl font-mono font-bold text-orange-400">
                                            {formatCurrency(data.summary.total_outstanding)}
                                        </p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Total Overdue */}
                        <Card className="bg-zinc-900/50 border-zinc-800">
                            <CardContent className="p-4">
                                <div className="flex items-center gap-3">
                                    <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${
                                        data.summary.total_overdue > 0 ? 'bg-red-500/20' : 'bg-green-500/20'
                                    }`}>
                                        <AlertCircle className={`w-5 h-5 ${
                                            data.summary.total_overdue > 0 ? 'text-red-400' : 'text-green-400'
                                        }`} />
                                    </div>
                                    <div>
                                        <p className="text-xs text-zinc-500 uppercase">Total Overdue</p>
                                        <p className={`text-2xl font-mono font-bold ${
                                            data.summary.total_overdue > 0 ? 'text-red-400' : 'text-green-400'
                                        }`}>
                                            {formatCurrency(data.summary.total_overdue)}
                                        </p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Oldest Outstanding */}
                        <Card className="bg-zinc-900/50 border-zinc-800">
                            <CardContent className="p-4">
                                <div className="flex items-center gap-3">
                                    <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${
                                        data.summary.oldest_outstanding_days > 45 
                                            ? 'bg-red-500/20' 
                                            : data.summary.oldest_outstanding_days > 30 
                                                ? 'bg-amber-500/20' 
                                                : 'bg-zinc-700/50'
                                    }`}>
                                        <Clock className={`w-5 h-5 ${
                                            data.summary.oldest_outstanding_days > 45 
                                                ? 'text-red-400' 
                                                : data.summary.oldest_outstanding_days > 30 
                                                    ? 'text-amber-400' 
                                                    : 'text-zinc-400'
                                        }`} />
                                    </div>
                                    <div>
                                        <p className="text-xs text-zinc-500 uppercase">Oldest Invoice</p>
                                        <p className={`text-2xl font-mono font-bold ${
                                            data.summary.oldest_outstanding_days > 45 
                                                ? 'text-red-400' 
                                                : data.summary.oldest_outstanding_days > 30 
                                                    ? 'text-amber-400' 
                                                    : 'text-white'
                                        }`}>
                                            {data.summary.oldest_outstanding_days} days
                                        </p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Avg Days To Pay */}
                        <Card className="bg-zinc-900/50 border-zinc-800">
                            <CardContent className="p-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-zinc-700/50 rounded-sm flex items-center justify-center">
                                        <TrendingUp className="w-5 h-5 text-zinc-400" />
                                    </div>
                                    <div>
                                        <p className="text-xs text-zinc-500 uppercase">Avg Days To Pay</p>
                                        <p className="text-2xl font-mono font-bold text-white">
                                            {data.summary.avg_days_to_pay} days
                                        </p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Aging Buckets */}
                    <Card className="bg-zinc-900/50 border-zinc-800">
                        <CardHeader className="pb-3">
                            <CardTitle className="text-lg text-white flex items-center gap-2">
                                <Calendar className="w-5 h-5 text-orange-500" />
                                Aging Buckets (Dispatch Revenue)
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-4 gap-4" data-testid="aging-buckets">
                                {Object.entries(data.aging_buckets).map(([key, bucket]) => (
                                    <div 
                                        key={key}
                                        className={`border rounded-sm p-4 ${getAgingBgColor(bucket.risk)}`}
                                        data-testid={`aging-bucket-${key}`}
                                    >
                                        <p className="text-sm text-zinc-400 mb-1">{bucket.label}</p>
                                        <p className={`text-2xl font-mono font-bold ${getAgingColor(bucket.risk)}`}>
                                            {formatCurrency(bucket.amount)}
                                        </p>
                                        <p className="text-xs text-zinc-500 mt-1">
                                            {bucket.count} invoice{bucket.count !== 1 ? 's' : ''}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Broker Receivables Table */}
                    <Card className="bg-zinc-900/50 border-zinc-800">
                        <CardHeader className="pb-3">
                            <CardTitle className="text-lg text-white flex items-center gap-2">
                                <Building2 className="w-5 h-5 text-orange-500" />
                                Broker Receivables
                                <Badge className="bg-zinc-700 text-zinc-300 border-0 ml-2">
                                    {data.broker_receivables.length} broker{data.broker_receivables.length !== 1 ? 's' : ''}
                                </Badge>
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {data.broker_receivables.length === 0 ? (
                                <p className="text-zinc-500 text-center py-8">No unpaid broker invoices</p>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full" data-testid="broker-receivables-table">
                                        <thead>
                                            <tr className="border-b border-zinc-800">
                                                <th className="text-left text-xs text-zinc-500 uppercase py-3 px-2">Broker</th>
                                                <th className="text-right text-xs text-zinc-500 uppercase py-3 px-2">Outstanding</th>
                                                <th className="text-right text-xs text-zinc-500 uppercase py-3 px-2">Overdue</th>
                                                <th className="text-right text-xs text-zinc-500 uppercase py-3 px-2">Avg Days</th>
                                                <th className="text-right text-xs text-zinc-500 uppercase py-3 px-2">Expected Days</th>
                                                <th className="text-right text-xs text-zinc-500 uppercase py-3 px-2">Late Count</th>
                                                <th className="text-right text-xs text-zinc-500 uppercase py-3 px-2">Oldest</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data.broker_receivables.map((broker, idx) => (
                                                <tr 
                                                    key={broker.broker_id}
                                                    className={`border-b border-zinc-800/50 hover:bg-zinc-800/30 ${
                                                        broker.overdue_amount > 0 ? 'bg-red-500/5' : ''
                                                    }`}
                                                >
                                                    <td className="py-3 px-2">
                                                        <span className="text-white font-medium">{broker.broker_name}</span>
                                                        {broker.overdue_amount > 0 && (
                                                            <Badge className="bg-red-500/20 text-red-400 border-0 ml-2 text-xs">
                                                                Overdue
                                                            </Badge>
                                                        )}
                                                    </td>
                                                    <td className="text-right py-3 px-2">
                                                        <span className="text-orange-400 font-mono font-semibold">
                                                            {formatCurrency(broker.total_outstanding)}
                                                        </span>
                                                    </td>
                                                    <td className="text-right py-3 px-2">
                                                        <span className={`font-mono ${broker.overdue_amount > 0 ? 'text-red-400' : 'text-zinc-500'}`}>
                                                            {formatCurrency(broker.overdue_amount)}
                                                        </span>
                                                    </td>
                                                    <td className="text-right py-3 px-2">
                                                        <span className="text-zinc-400 font-mono">
                                                            {broker.avg_days_to_pay ?? '-'}
                                                        </span>
                                                    </td>
                                                    <td className="text-right py-3 px-2">
                                                        <span className="text-zinc-400 font-mono">
                                                            {broker.expected_days_to_pay ?? '-'}
                                                        </span>
                                                    </td>
                                                    <td className="text-right py-3 px-2">
                                                        <span className={`font-mono ${broker.late_payment_count > 0 ? 'text-amber-400' : 'text-zinc-500'}`}>
                                                            {broker.late_payment_count}
                                                        </span>
                                                    </td>
                                                    <td className="text-right py-3 px-2">
                                                        <span className={`font-mono ${
                                                            broker.oldest_days > 45 ? 'text-red-400' : 
                                                            broker.oldest_days > 30 ? 'text-amber-400' : 'text-zinc-400'
                                                        }`}>
                                                            {broker.oldest_days}d
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Invoice Details Table */}
                    <Card className="bg-zinc-900/50 border-zinc-800">
                        <CardHeader className="pb-3">
                            <CardTitle className="text-lg text-white flex items-center gap-2">
                                <FileText className="w-5 h-5 text-orange-500" />
                                Unpaid Invoices
                                <Badge className="bg-zinc-700 text-zinc-300 border-0 ml-2">
                                    {data.invoice_details.length} invoice{data.invoice_details.length !== 1 ? 's' : ''}
                                </Badge>
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {data.invoice_details.length === 0 ? (
                                <div className="text-center py-8">
                                    <FileText className="w-10 h-10 text-green-400 mx-auto mb-3" />
                                    <p className="text-green-400 font-medium">All Clear!</p>
                                    <p className="text-zinc-500 text-sm">No unpaid invoices</p>
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full" data-testid="invoice-details-table">
                                        <thead>
                                            <tr className="border-b border-zinc-800">
                                                <th className="text-left text-xs text-zinc-500 uppercase py-3 px-2">Load ID</th>
                                                <th className="text-left text-xs text-zinc-500 uppercase py-3 px-2">Broker</th>
                                                <th className="text-right text-xs text-zinc-500 uppercase py-3 px-2">Dispatch Fee</th>
                                                <th className="text-left text-xs text-zinc-500 uppercase py-3 px-2">Invoice Date</th>
                                                <th className="text-left text-xs text-zinc-500 uppercase py-3 px-2">Due Date</th>
                                                <th className="text-right text-xs text-zinc-500 uppercase py-3 px-2">Days Out</th>
                                                <th className="text-left text-xs text-zinc-500 uppercase py-3 px-2">Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data.invoice_details.map((invoice) => (
                                                <tr 
                                                    key={invoice.invoice_id}
                                                    className={`border-b border-zinc-800/50 hover:bg-zinc-800/30 ${
                                                        invoice.is_overdue ? 'bg-red-500/5' : 
                                                        invoice.is_near_due ? 'bg-amber-500/5' : ''
                                                    }`}
                                                    data-testid={`invoice-row-${invoice.invoice_id}`}
                                                >
                                                    <td className="py-3 px-2">
                                                        <span className="text-zinc-300 font-mono text-sm">{invoice.load_id}</span>
                                                    </td>
                                                    <td className="py-3 px-2">
                                                        <span className="text-white">{invoice.broker_name}</span>
                                                    </td>
                                                    <td className="text-right py-3 px-2">
                                                        <span className="text-green-400 font-mono font-semibold">
                                                            {formatCurrency(invoice.dispatch_fee)}
                                                        </span>
                                                    </td>
                                                    <td className="py-3 px-2">
                                                        <span className="text-zinc-400 text-sm">{formatDate(invoice.invoice_date)}</span>
                                                    </td>
                                                    <td className="py-3 px-2">
                                                        <span className={`text-sm ${
                                                            invoice.is_overdue ? 'text-red-400' : 
                                                            invoice.is_near_due ? 'text-amber-400' : 'text-zinc-400'
                                                        }`}>
                                                            {formatDate(invoice.due_date)}
                                                        </span>
                                                    </td>
                                                    <td className="text-right py-3 px-2">
                                                        <span className={`font-mono ${
                                                            invoice.days_outstanding > 45 ? 'text-red-400 font-bold' : 
                                                            invoice.days_outstanding > 30 ? 'text-amber-400' : 'text-zinc-400'
                                                        }`}>
                                                            {invoice.days_outstanding}
                                                        </span>
                                                    </td>
                                                    <td className="py-3 px-2">
                                                        {invoice.is_overdue ? (
                                                            <Badge className="bg-red-500/20 text-red-400 border-0">
                                                                <AlertTriangle className="w-3 h-3 mr-1" />
                                                                Overdue
                                                            </Badge>
                                                        ) : invoice.is_near_due ? (
                                                            <Badge className="bg-amber-500/20 text-amber-400 border-0">
                                                                <Clock className="w-3 h-3 mr-1" />
                                                                Due Soon
                                                            </Badge>
                                                        ) : (
                                                            <Badge className="bg-zinc-700 text-zinc-300 border-0">
                                                                {invoice.status}
                                                            </Badge>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </>
            )}
                </TabsContent>

                {/* Dispatcher Revenue Tab - Big T's Logistics */}
                <TabsContent value="dispatcher" className="mt-6">
                    {dispatcherLoading ? (
                        <div className="flex flex-col items-center justify-center py-12">
                            <Loader2 className="w-10 h-10 text-orange-500 animate-spin mb-4" />
                            <p className="text-zinc-400">Loading dispatcher revenue data...</p>
                        </div>
                    ) : !dispatcherData ? (
                        <Card className="bg-zinc-900/50 border-zinc-800">
                            <CardContent className="flex flex-col items-center justify-center py-12">
                                <Truck className="w-12 h-12 text-zinc-600 mb-4" />
                                <h3 className="text-lg font-medium text-white mb-2">No Dispatcher Data</h3>
                                <p className="text-zinc-400 text-center">
                                    Delivered loads will appear here for invoicing.
                                </p>
                            </CardContent>
                        </Card>
                    ) : (
                        <>
                            {/* Big T's Logistics Header */}
                            <Card className="bg-gradient-to-r from-orange-500/20 to-amber-500/10 border-orange-500/30 mb-6">
                                <CardContent className="py-4">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <Truck className="w-8 h-8 text-orange-400" />
                                            <div>
                                                <h2 className="text-xl font-bold text-white">Big T's Logistics</h2>
                                                <p className="text-zinc-400 text-sm">Dispatch Fee Revenue Tracking</p>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-xs text-zinc-500">Ready to Invoice</p>
                                            <Badge className="bg-orange-500 text-white">
                                                {dispatcherData.ready_to_invoice_count} loads
                                            </Badge>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Dispatcher Summary Cards */}
                            <div className="grid grid-cols-4 gap-4 mb-6" data-testid="dispatcher-summary-cards">
                                <Card className="bg-zinc-900/50 border-zinc-800">
                                    <CardContent className="p-4">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 bg-green-500/20 rounded-sm flex items-center justify-center">
                                                <DollarSign className="w-5 h-5 text-green-400" />
                                            </div>
                                            <div>
                                                <p className="text-xs text-zinc-500 uppercase">Total Fees Earned</p>
                                                <p className="text-2xl font-mono font-bold text-green-400">
                                                    {formatCurrency(dispatcherData.totals?.total_fees_earned)}
                                                </p>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>

                                <Card className="bg-zinc-900/50 border-zinc-800">
                                    <CardContent className="p-4">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 bg-amber-500/20 rounded-sm flex items-center justify-center">
                                                <FileText className="w-5 h-5 text-amber-400" />
                                            </div>
                                            <div>
                                                <p className="text-xs text-zinc-500 uppercase">Uninvoiced</p>
                                                <p className="text-2xl font-mono font-bold text-amber-400">
                                                    {formatCurrency(dispatcherData.totals?.total_unpaid)}
                                                </p>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>

                                <Card className="bg-zinc-900/50 border-zinc-800">
                                    <CardContent className="p-4">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 bg-blue-500/20 rounded-sm flex items-center justify-center">
                                                <Clock className="w-5 h-5 text-blue-400" />
                                            </div>
                                            <div>
                                                <p className="text-xs text-zinc-500 uppercase">Invoiced (Awaiting)</p>
                                                <p className="text-2xl font-mono font-bold text-blue-400">
                                                    {formatCurrency(dispatcherData.totals?.total_invoiced)}
                                                </p>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>

                                <Card className="bg-zinc-900/50 border-zinc-800">
                                    <CardContent className="p-4">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 bg-green-500/20 rounded-sm flex items-center justify-center">
                                                <CheckCircle className="w-5 h-5 text-green-400" />
                                            </div>
                                            <div>
                                                <p className="text-xs text-zinc-500 uppercase">Collected</p>
                                                <p className="text-2xl font-mono font-bold text-green-400">
                                                    {formatCurrency(dispatcherData.totals?.total_paid)}
                                                </p>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            </div>

                            {/* Automated Reserves Panel */}
                            <Card className="bg-gradient-to-r from-purple-500/10 to-blue-500/10 border-purple-500/30 mb-6">
                                <CardHeader className="pb-3">
                                    <CardTitle className="text-lg text-white flex items-center gap-2">
                                        <TrendingUp className="w-5 h-5 text-purple-400" />
                                        Automated Reserves (Calculated on Dispatch Fees)
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="grid grid-cols-4 gap-4" data-testid="reserve-cards">
                                        {/* Total Dispatch Fees (Input) */}
                                        <div className="bg-zinc-800/50 rounded-lg p-4 border border-zinc-700">
                                            <div className="flex items-center gap-2 mb-2">
                                                <DollarSign className="w-4 h-4 text-green-400" />
                                                <p className="text-xs text-zinc-400 uppercase">Total Dispatch Fees</p>
                                            </div>
                                            <p className="text-2xl font-mono font-bold text-green-400">
                                                {formatCurrency(dispatcherData.totals?.total_fees_earned)}
                                            </p>
                                            <p className="text-xs text-zinc-500 mt-1">
                                                Commission (7-10% of Gross)
                                            </p>
                                        </div>

                                        {/* Tax Reserve (25% of Fee) */}
                                        <div className="bg-red-500/10 rounded-lg p-4 border border-red-500/30">
                                            <div className="flex items-center gap-2 mb-2">
                                                <AlertCircle className="w-4 h-4 text-red-400" />
                                                <p className="text-xs text-zinc-400 uppercase">Tax Reserve ({dispatcherData.reserve_rates?.tax_percent || 25}%)</p>
                                            </div>
                                            <p className="text-2xl font-mono font-bold text-red-400">
                                                {formatCurrency(dispatcherData.totals?.total_tax_reserve)}
                                            </p>
                                            <p className="text-xs text-zinc-500 mt-1">
                                                Automated quarterly estimate
                                            </p>
                                        </div>

                                        {/* Equipment Fund (10% of Fee) */}
                                        <div className="bg-blue-500/10 rounded-lg p-4 border border-blue-500/30">
                                            <div className="flex items-center gap-2 mb-2">
                                                <Truck className="w-4 h-4 text-blue-400" />
                                                <p className="text-xs text-zinc-400 uppercase">Equipment Fund ({dispatcherData.reserve_rates?.equipment_fund_percent || 10}%)</p>
                                            </div>
                                            <p className="text-2xl font-mono font-bold text-blue-400">
                                                {formatCurrency(dispatcherData.totals?.total_equipment_fund)}
                                            </p>
                                            <p className="text-xs text-zinc-500 mt-1">
                                                Maintenance & upgrades
                                            </p>
                                        </div>

                                        {/* Net Take-Home */}
                                        <div className="bg-green-500/10 rounded-lg p-4 border border-green-500/30">
                                            <div className="flex items-center gap-2 mb-2">
                                                <CheckCircle className="w-4 h-4 text-green-400" />
                                                <p className="text-xs text-zinc-400 uppercase">Net Take-Home</p>
                                            </div>
                                            <p className="text-2xl font-mono font-bold text-green-400">
                                                {formatCurrency(dispatcherData.totals?.total_net_takehome)}
                                            </p>
                                            <p className="text-xs text-zinc-500 mt-1">
                                                After all reserves
                                            </p>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Dispatcher Aging Buckets */}
                            <Card className="bg-zinc-900/50 border-zinc-800 mb-6">
                                <CardHeader className="pb-3">
                                    <CardTitle className="text-lg text-white flex items-center gap-2">
                                        <Calendar className="w-5 h-5 text-orange-500" />
                                        Invoiced Fee Aging (7-Day Terms)
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="grid grid-cols-4 gap-4" data-testid="dispatcher-aging-buckets">
                                        <div className="border border-green-500/30 bg-green-500/10 rounded-sm p-4">
                                            <p className="text-sm text-zinc-400 mb-1">0-15 Days</p>
                                            <p className="text-2xl font-mono font-bold text-green-400">
                                                {formatCurrency(dispatcherData.aging_buckets?.['0-15']?.total)}
                                            </p>
                                            <p className="text-xs text-zinc-500 mt-1">
                                                {dispatcherData.aging_buckets?.['0-15']?.count || 0} invoice(s)
                                            </p>
                                        </div>
                                        <div className="border border-amber-500/30 bg-amber-500/10 rounded-sm p-4">
                                            <p className="text-sm text-zinc-400 mb-1">16-30 Days</p>
                                            <p className="text-2xl font-mono font-bold text-amber-400">
                                                {formatCurrency(dispatcherData.aging_buckets?.['16-30']?.total)}
                                            </p>
                                            <p className="text-xs text-zinc-500 mt-1">
                                                {dispatcherData.aging_buckets?.['16-30']?.count || 0} invoice(s)
                                            </p>
                                        </div>
                                        <div className="border border-orange-500/30 bg-orange-500/10 rounded-sm p-4">
                                            <p className="text-sm text-zinc-400 mb-1">31-45 Days</p>
                                            <p className="text-2xl font-mono font-bold text-orange-400">
                                                {formatCurrency(dispatcherData.aging_buckets?.['31-45']?.total)}
                                            </p>
                                            <p className="text-xs text-zinc-500 mt-1">
                                                {dispatcherData.aging_buckets?.['31-45']?.count || 0} invoice(s)
                                            </p>
                                        </div>
                                        <div className="border border-red-500/30 bg-red-500/10 rounded-sm p-4">
                                            <p className="text-sm text-zinc-400 mb-1">45+ Days</p>
                                            <p className="text-2xl font-mono font-bold text-red-400">
                                                {formatCurrency(dispatcherData.aging_buckets?.['45+']?.total)}
                                            </p>
                                            <p className="text-xs text-zinc-500 mt-1">
                                                {dispatcherData.aging_buckets?.['45+']?.count || 0} invoice(s)
                                            </p>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Dispatcher Loads Table */}
                            <Card className="bg-zinc-900/50 border-zinc-800">
                                <CardHeader className="pb-3">
                                    <CardTitle className="text-lg text-white flex items-center gap-2">
                                        <FileText className="w-5 h-5 text-orange-500" />
                                        Dispatcher Fee Loads
                                        <Badge className="bg-zinc-700 text-zinc-300 border-0 ml-2">
                                            {dispatcherData.loads?.length || 0} load(s)
                                        </Badge>
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    {!dispatcherData.loads?.length ? (
                                        <div className="text-center py-8">
                                            <Truck className="w-10 h-10 text-zinc-600 mx-auto mb-3" />
                                            <p className="text-zinc-400">No delivered loads yet</p>
                                        </div>
                                    ) : (
                                        <div className="overflow-x-auto">
                                            <table className="w-full" data-testid="dispatcher-loads-table">
                                                <thead>
                                                    <tr className="border-b border-zinc-800">
                                                        <th className="text-left text-xs text-zinc-500 uppercase py-3 px-2">Load ID</th>
                                                        <th className="text-left text-xs text-zinc-500 uppercase py-3 px-2">Route</th>
                                                        <th className="text-left text-xs text-zinc-500 uppercase py-3 px-2">Truck</th>
                                                        <th className="text-right text-xs text-zinc-500 uppercase py-3 px-2">Gross</th>
                                                        <th className="text-right text-xs text-zinc-500 uppercase py-3 px-2">Commission</th>
                                                        <th className="text-right text-xs text-zinc-500 uppercase py-3 px-2">Dispatch Fee</th>
                                                        <th className="text-left text-xs text-zinc-500 uppercase py-3 px-2">Status</th>
                                                        <th className="text-left text-xs text-zinc-500 uppercase py-3 px-2">Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {dispatcherData.loads.map((load) => (
                                                        <tr 
                                                            key={load.load_id}
                                                            className="border-b border-zinc-800/50 hover:bg-zinc-800/30"
                                                            data-testid={`dispatcher-load-${load.load_id}`}
                                                        >
                                                            <td className="py-3 px-2">
                                                                <span className="text-zinc-300 font-mono text-sm">{load.load_id?.slice(-10)}</span>
                                                            </td>
                                                            <td className="py-3 px-2">
                                                                <span className="text-white text-sm">{load.origin} → {load.destination}</span>
                                                            </td>
                                                            <td className="py-3 px-2">
                                                                <span className="text-zinc-400 text-sm">{load.truck_name || load.truck_id?.slice(-8)}</span>
                                                            </td>
                                                            <td className="text-right py-3 px-2">
                                                                <span className="text-white font-mono">{formatCurrency(load.gross)}</span>
                                                            </td>
                                                            <td className="text-right py-3 px-2">
                                                                <span className="text-zinc-400 font-mono">{load.commission_rate}%</span>
                                                            </td>
                                                            <td className="text-right py-3 px-2">
                                                                <span className="text-green-400 font-mono font-semibold">
                                                                    {formatCurrency(load.dispatcher_fee)}
                                                                </span>
                                                            </td>
                                                            <td className="py-3 px-2">
                                                                {load.invoice_status === 'paid' ? (
                                                                    <Badge className="bg-green-500/20 text-green-400 border-0">
                                                                        <CheckCircle className="w-3 h-3 mr-1" />
                                                                        Paid
                                                                    </Badge>
                                                                ) : load.invoice_status === 'invoiced' ? (
                                                                    <Badge className="bg-blue-500/20 text-blue-400 border-0">
                                                                        <Clock className="w-3 h-3 mr-1" />
                                                                        Invoiced
                                                                    </Badge>
                                                                ) : (
                                                                    <Badge className="bg-amber-500/20 text-amber-400 border-0">
                                                                        Ready to Invoice
                                                                    </Badge>
                                                                )}
                                                            </td>
                                                            <td className="py-3 px-2">
                                                                {load.invoice_status === 'unpaid' && (
                                                                    <Button
                                                                        size="sm"
                                                                        onClick={() => handleGenerateDispatchInvoice(load.load_id)}
                                                                        disabled={generatingInvoice[load.load_id]}
                                                                        className="bg-orange-500 hover:bg-orange-600 text-white text-xs"
                                                                        data-testid={`generate-invoice-${load.load_id}`}
                                                                    >
                                                                        {generatingInvoice[load.load_id] ? (
                                                                            <Loader2 className="w-3 h-3 animate-spin" />
                                                                        ) : (
                                                                            <>
                                                                                <Download className="w-3 h-3 mr-1" />
                                                                                Generate Invoice
                                                                            </>
                                                                        )}
                                                                    </Button>
                                                                )}
                                                                {load.invoice_status === 'invoiced' && (
                                                                    <Button
                                                                        size="sm"
                                                                        variant="outline"
                                                                        onClick={() => handleMarkPaid(load.load_id)}
                                                                        className="border-green-500 text-green-400 hover:bg-green-500/10 text-xs"
                                                                        data-testid={`mark-paid-${load.load_id}`}
                                                                    >
                                                                        <CheckCircle className="w-3 h-3 mr-1" />
                                                                        Mark Paid
                                                                    </Button>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        </>
                    )}
                </TabsContent>
            </Tabs>
        </div>
    );
}
